import React, { useRef, useState, useEffect } from 'react';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import CircularProgress from '@mui/material/CircularProgress';
import Input from 'ui/components/Input';
import Switch from 'ui/components/Switch';
import TabNavigation from 'ui/pages/program/components/TabNavigation';
import Scopes from './components/scopes/Scopes';
import Themes from './components/themes/Themes';
import Users from './components/users/Users';
import EmailTemplates from './components/emailTemplates/EmailTemplates';
import UploadField from 'ui/components/form/UploadField';
import TargetTemplateItems from './components/targetTemplateItems/TargetTemplateItems';
import TextField from '@mui/material/TextField';
import Departments from 'ui/pages/settings/components/departments/Departments';
import GoBackButton from 'ui/components/shared/GoBackButton';
// Validation
import validation from './validators/company.validator';
import SeniorityLevels from 'ui/pages/settings/components/seniorityLevels/SeniorityLevels';
// Navigation
import { useNavigate } from 'react-router-dom';

import { useEditCompany, useCompanyData } from 'hooks/usecase/useCompany';
import { useMeQuery } from 'hooks/queries';
import { useTranslation } from 'react-i18next';
import { LABEL_INPUT_LINK_APPLICATION } from 'litterals';
import useProgram from 'hooks/usecase/useProgram';
import Resources from 'ui/pages/program/components/Resources';
import api from 'api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import HomeRH from 'ui/pages/programs/ProgramsRh';
import AddIcon from '@mui/icons-material/Add';
import useViewSubscription from 'hooks/usecase/useViewSubscription';
import { roleList } from 'utils/constUtils';
import { Case, Switch as SwitchMe } from 'ui/components/system/SwitchCase';
import styles from 'ui/pages/program/components/Header/Header.styles.js';
import { useParams } from 'react-router-dom';
import { useCompanyUploadDocument } from 'hooks/usecase/useCompanyResource';
import ResourceModal from 'ui/pages/program/components/Resources/ResourceModal';
import { useUploadDocumentUpdate } from 'hooks/usecase/useCreateProgramDocument';
import { getUrlApplicationLink, getUrlEvergreenLink } from 'utils/company';
import EditButton from '../../../program/components/EditButton/EditButton';
import EditMentorModal from '../../../program/components/EditMentorModal/EditMentorModal';
import SelfRegistration from 'ui/pages/settings/components/self-registration/SelfRegistration';
import { margin } from '@mui/system';
import ProgramsRh from 'ui/pages/programs/ProgramsRh';

const EditCompany = ({
  company = {
    name: '',
    hasTerms: false,
    linkTerms: '',
    feedbackLink: '',
  },
  onCancel,
}) => {
  const { data: user } = useMeQuery();
  // Refs
  const refName = useRef();
  const refLinkTerms = useRef();
  const refFeedbackLin = useRef();
  const refExpertLabel = useRef();
  const refClientLabel = useRef();
  const refSessionLabel = useRef();
  const refFeedbackLinkMentor = useRef();
  const refIsSelfRegistrationActivated = useRef();
  const refIsManualMatchingActivated = useRef();
  const refIsEvergreenActivated = useRef();
  const refNavbarColorSelfInscription = useRef();
  const refCustomHeaderPicture = useRef();
  const refdisplayActivities = useRef();
  const refsendEmailProgramEndingActivated = useRef();
  const refSelfRegistrationSendEmailProvider = useRef();
  const refHasTerms = useRef();
  const inputRef = useRef();
  const delayProgramDeletion = useRef();
  const refOptionalScope = useRef();
  const refExportDocumentLink = useRef();

  const [selectedTab, setSelectedTab] = useState('tab-1');
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);

  let { companyId } = useParams();
  const {
    state: uploadDocumentState,
    actions: uploadDocumentActions,
    formState: uploadDocumentFormState,
  } = useCompanyUploadDocument(companyId);

  const [openThemeModal, setOpenThemeModal] = useState(false);
  const [openMentorModal, setOpenMentorModal] = useState(false);

  const onClickExpert = () => {
    if (status === 'pending') {
      setOpenEditMentorImpossible(true);
    } else {
      setOpenMentorModal(true);
    }
  };

  const navigate = useNavigate();
  const {
    isLoading,
    control,
    watch,
    submitEditCompanyWithNavigate,
    submitEditLogoCompany,
    company: companyData,
  } = useCompanyData(companyId);
  const [loadedLogo, setLoadedLogo] = useState(false);

  const { t } = useTranslation();

  const getOptionsTab = () => {
    return [
      { id: 'tab-1', text: 'General informations', selected: true },
      { id: 'tab-10', text: 'Suivi des Binômes', selected: false },
      { id: 'tab-2', text: 'Scopes', selected: false },
      { id: 'tab-3', text: 'Themes', selected: false },
      { id: 'tab-4', text: 'Users', selected: false },
      { id: 'tab-5', text: 'Email templates', selected: false },
      { id: 'tab-6', text: 'Departments', selected: false, disabled: false },
      { id: 'tab-7', text: 'Seniority Levels', selected: false, disabled: false },
      { id: 'tab-8', text: 'Target Templates', selected: false, disabled: false },
      { id: 'tab-9', text: 'Resources', selected: false, disabled: false },
    ];
  };

  const renderComponent = () => {
    switch (selectedTab) {
      case 'tab-2':
        return <Scopes />;
      case 'tab-3':
        return <Themes user={user} />;
      case 'tab-4':
        return <Users user={user} company={companyData} />;
      case 'tab-5':
        return <EmailTemplates user={user} company={companyData} />;
      case 'tab-6':
        return <Departments user={user} isAdmin={true} />;
      case 'tab-7':
        return <SeniorityLevels user={user} isAdmin={true} />;
      case 'tab-8':
        return <TargetTemplateItems user={user} />;
      case 'tab-9':
        return (
          <>
            {/* Button for adding resource : In progress + Resource request needs to send only resources of the company
              <Box sx={{ backgroundColor: 'white', marginTop: '40px', padding: '0 24px 24px 24px' }}>
                <Box sx={{ alignItems: 'center', display: 'flex' }}>
                  <Button startIcon={<AddIcon />} variant="tertiary" onClick={uploadDocumentActions.handleShowDialog}>
                    {t('add_ressource')}
                  </Button>
                </Box>
              </Box>
            */}
            <ResourceModal
              programId={companyId}
              actions={uploadDocumentActions}
              state={uploadDocumentState}
              open={uploadDocumentState.nav.showResourceDialog}
              onSubmit={uploadDocumentActions.submit}
              onClose={uploadDocumentActions.close}
              loading={uploadDocumentFormState.isSubmitting}
            />
            <Resources user={user} isRh={true} programId={companyId} />
          </>
        );
      case 'tab-10':
        return (
          <ProgramsRh
            company={companyData}
            containerMargin="0px"
            containerElevation={0}
            isShowCreateProgram={false}
            isShowBackButton={false}
            isDeactivatesClickingOnProgram={true}
            user={{ ...user, company: companyData }}
          />
        );
      default:
        return null;
    }
  };

  const handleUpload = () => {
    inputRef?.current?.click();
  };

  if (isLoading)
    return (
      <Stack spacing={1} sx={{ padding: '30px 40px' }}>
        <Skeleton variant="rounded" height={60} />
        <Skeleton variant="rounded" height={60} />
        <Skeleton variant="rounded" height={60} />
        <Skeleton variant="rounded" height={60} />
        <Skeleton variant="rounded" height={60} />
        <Skeleton variant="rounded" height={60} />
        <Skeleton variant="rounded" height={100} />
      </Stack>
    );

  function isObject(variable) {
    return typeof variable === 'object' && variable !== null;
  }

  const applicationLink = companyData?.applicationLink ? getUrlApplicationLink(companyData.applicationLink) : '';

  return (
    <>
      <Box sx={{ margin: '10px 33px' }}>
        <GoBackButton
          onClick={() => {
            navigate(-1);
          }}
        />
      </Box>
      <Stack
        sx={{
          overflowY: 'scroll',
          maxHeight: '730px',
          padding: '20px',
          margin: '0px 33px',
          backgroundColor: 'white',
          '&::-webkit-scrollbar': {
            '-webkit-appearance': 'none',
            width: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: '25px',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            '-webkit-box-shadow': '0 0 1px rgba(255, 255, 255, 0.5)',
          },
        }}>
        <Stack>
          <Box sx={{ padding: '8px 0px', display: 'flex', alignItems: 'center' }}>
            {isObject(watch('logo')) ? (
              <Box
                sx={{
                  height: '44.38px',
                  width: '76.04px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}>
                <CircularProgress size="1rem" color="secondary" />{' '}
              </Box>
            ) : (
              <Box
                height={'44.38px'}
                width={'76.04px'}
                component="img"
                name="logo"
                src={
                  watch('logo')
                    ? `${watch('logo')}?${Date.now()}`
                    : 'https://res.cloudinary.com/dirpe8qjq/image/upload/v1701108669/hvcfy7yngstb5qfuod9n.png'
                }
                sx={{ objectFit: 'contain' }}
                onLoad={() => setLoadedLogo(false)}
                onLoadStart={() => setLoadedLogo(true)}
                loading="lazy"
              />
            )}
            <Box sx={{ width: '24px' }} />
            <Typography
              sx={{
                color: 'primary.main',
                fontSize: '40px',
                fontWeight: 800,
                lineHeight: '32px',
                whiteSpace: 'nowrap',
              }}>
              {watch('name')}
            </Typography>
            <Box sx={{ width: '20px' }} />
            <Box style={{ display: 'none' }}>
              <UploadField
                label="logo"
                name="logo"
                folder={'company-logo/'}
                fileId={companyId}
                control={control}
                ref={inputRef}
                setDisabledButton={() => {}}
                accept={'picture'}
                submitForm={submitEditLogoCompany}
                onUploadSuccess={() => {}}
              />
            </Box>
            <Button
              variant="contained"
              sx={{ backgroundColor: 'primary.dark', whiteSpace: 'nowrap', width: '82px' }}
              onClick={handleUpload}
              size="small">
              {'Edit logo'}
            </Button>
          </Box>
          <Box sx={{ paddingLeft: '90px' }}>
            <TabNavigation
              options={getOptionsTab()}
              onChange={(tab) => {
                setSelectedTab(tab);
              }}
            />
          </Box>
          {selectedTab !== 'tab-1' ? (
            renderComponent()
          ) : (
            <Box sx={{ paddingLeft: '90px' }}>
              <Box sx={{ height: '20px' }} />
              <Box
                sx={{
                  padding: '8px 0px',
                  display: 'flex',
                  maxWidth: '500px',
                  height: '70px',
                }}>
                <Input
                  ref={refName}
                  rules={validation.name}
                  control={control}
                  variant="outlined"
                  label="Name"
                  name="name"
                />
              </Box>
              <Box sx={{ padding: '8px 0px', display: 'flex', maxWidth: '500px', height: '70px' }}>
                <Typography
                  style={{
                    color: '#000000DE',
                    fontSize: '12px',
                    fontWeight: 500,
                    lineHeight: '32px',
                  }}>
                  {'Has terms'}
                </Typography>
                <Switch
                  ref={refHasTerms}
                  control={control}
                  variant="outlined"
                  label="hasTerms"
                  name="hasTerms"
                  fullWidth
                />
              </Box>
              <Box sx={{ padding: '8px 0px', display: 'flex', maxWidth: '500px', height: '70px' }}>
                <Input
                  ref={refLinkTerms}
                  control={control}
                  variant="outlined"
                  label="Link terms"
                  name="linkTerms"
                  rules={{ required: !watch('hasTerms') ? false : 'This field is required' }}
                  fullWidth
                />
              </Box>
              <Box sx={{ padding: '8px 0px', display: 'flex', maxWidth: '500px', height: '70px' }}>
                <Input
                  ref={refFeedbackLin}
                  control={control}
                  variant="outlined"
                  label="Feedback link"
                  name="feedbackLink"
                  rules={validation.feedbackLin}
                  fullWidth
                />
              </Box>
              <Box sx={{ padding: '8px 0px', display: 'flex', maxWidth: '500px', height: '70px' }}>
                <Input
                  ref={refFeedbackLinkMentor}
                  control={control}
                  variant="outlined"
                  label="Feedback Link Mentor"
                  name="feedbackLinkMentor"
                  fullWidth
                />
              </Box>
              <Box sx={{ padding: '8px 0px', display: 'flex', maxWidth: '500px', height: '70px' }}>
                <Input
                  ref={refExpertLabel}
                  control={control}
                  variant="outlined"
                  label="Expert label"
                  name="expertLabel"
                  rules={validation.expertLabel}
                  fullWidth
                />
              </Box>
              <Box sx={{ padding: '8px 0px', display: 'flex', maxWidth: '500px', height: '70px' }}>
                <Input
                  ref={refClientLabel}
                  control={control}
                  variant="outlined"
                  label="Client label"
                  name="clientLabel"
                  rules={validation.clientLabel}
                  fullWidth
                />
              </Box>
              <Box sx={{ padding: '8px 0px', display: 'flex', maxWidth: '500px', height: '70px' }}>
                <Input
                  ref={refSessionLabel}
                  control={control}
                  variant="outlined"
                  label="Session label"
                  name="sessionLabel"
                  rules={validation.sessionLabel}
                  fullWidth
                />
              </Box>
              <Box sx={{ padding: '8px 0px', display: 'flex', maxWidth: '500px', height: '70px' }}>
                <Input
                  ref={delayProgramDeletion}
                  control={control}
                  variant="outlined"
                  label="Delay before deactivated program deletion"
                  name="deactivatedPrograms_delay_before_deletion"
                  rules={validation.deactivatedPrograms_delay_before_deletion}
                  fullWidth
                />
              </Box>
              <Box sx={{ padding: '8px 0px', display: 'flex', maxWidth: '500px', height: '70px' }}>
                <TextField
                  sx={{
                    container: {
                      width: '100%',
                      '& .MuiFilledInput-root': { bgcolor: 'white' },
                      '& .MuiInputBase-root.MuiFilledInput-root:before': { borderBottom: 'none' },
                    },
                    inputProps: { style: { fontSize: 15 } },
                    InputLabelProps: { style: { fontSize: 15 } },
                  }}
                  variant="outlined"
                  label="Evergreen link"
                  fullWidth
                  inputProps={{ readOnly: true }}
                  value={getUrlEvergreenLink(companyData && companyData.evergreenLink ? companyData.evergreenLink : '')}
                />
              </Box>
              <Box sx={{ padding: '8px 0px', display: 'flex', maxWidth: '500px', height: '70px' }}>
                <TextField
                  sx={{
                    container: {
                      width: '100%',
                      '& .MuiFilledInput-root': { bgcolor: 'white' },
                      '& .MuiInputBase-root.MuiFilledInput-root:before': { borderBottom: 'none' },
                    },
                    inputProps: { style: { fontSize: 15 } },
                    InputLabelProps: { style: { fontSize: 15 } },
                  }}
                  variant="outlined"
                  label={t(LABEL_INPUT_LINK_APPLICATION)}
                  fullWidth
                  inputProps={{ readOnly: true }}
                  value={applicationLink}
                />
              </Box>
              <Box sx={{ padding: '8px 0px', display: 'flex' }}>
                <Typography
                  style={{
                    color: '#000000DE',
                    fontSize: '12px',
                    fontWeight: 500,
                    lineHeight: '32px',
                  }}>
                  {'Self Registration'}
                </Typography>
                <Switch
                  ref={refIsSelfRegistrationActivated}
                  control={control}
                  variant="outlined"
                  label="isSelfregistrationActivated"
                  name="selfRegistrationActivated"
                  fullWidth
                />
              </Box>
              <Box sx={{ padding: '8px 0px', display: 'flex' }}>
                <Typography
                  style={{
                    color: '#000000DE',
                    fontSize: '12px',
                    fontWeight: 500,
                    lineHeight: '32px',
                  }}>
                  {'Manual Matching'}
                </Typography>
                <Switch
                  ref={refIsManualMatchingActivated}
                  control={control}
                  variant="outlined"
                  label="refIsManualMatchingActivated"
                  name="manualMatchingActivated"
                  fullWidth
                />
              </Box>
              <Box sx={{ padding: '8px 0px', display: 'flex' }}>
                <Typography
                  style={{
                    color: '#000000DE',
                    fontSize: '12px',
                    fontWeight: 500,
                    lineHeight: '32px',
                  }}>
                  {'Evergreen Matching'}
                </Typography>
                <Switch
                  ref={refIsEvergreenActivated}
                  control={control}
                  variant="outlined"
                  label="refIsEvergreenActivated"
                  name="evergreenActivated"
                  fullWidth
                />
              </Box>
              <Box sx={{ padding: '0px 0px 50px 0px', maxWidth: '500px', height: '100px' }}>
                <SelfRegistration
                  selfRegistrationLinkCode={companyData.selfRegistrationLink}
                  applicationLinkCode={companyData?.applicationLink}
                  companyId={companyId}
                />
              </Box>

              <Box sx={{ padding: '8px 0px', display: 'flex', maxWidth: '500px', height: '70px' }}>
                <Input
                  ref={refNavbarColorSelfInscription}
                  control={control}
                  variant="outlined"
                  label="Navbar Color"
                  name="navbarColor_selfInscription"
                  rules={validation.navbarColor_selfInscription}
                  fullWidth
                />
              </Box>
              <Box sx={{ padding: '8px 0px', display: 'flex', maxWidth: '500px', height: '70px' }}>
                <Input
                  ref={refCustomHeaderPicture}
                  control={control}
                  variant="outlined"
                  label="Custom Header Picture"
                  name="customHeaderPicture"
                  fullWidth
                />
              </Box>
              <Box sx={{ padding: '8px 0px', display: 'flex', maxWidth: '500px', height: '70px' }}>
                <Typography
                  style={{
                    color: '#000000DE',
                    fontSize: '12px',
                    fontWeight: 500,
                    lineHeight: '32px',
                  }}>
                  {'Display Activities'}
                </Typography>
                <Switch
                  ref={refdisplayActivities}
                  control={control}
                  variant="outlined"
                  label="displayActivities"
                  name="displayActivities"
                  fullWidth
                />
              </Box>
              <Box sx={{ padding: '8px 0px', display: 'flex', maxWidth: '500px', height: '70px' }}>
                <Typography
                  style={{
                    color: '#000000DE',
                    fontSize: '12px',
                    fontWeight: 500,
                    lineHeight: '32px',
                  }}>
                  {'Send emails at the end of a program'}
                </Typography>
                <Switch
                  ref={refsendEmailProgramEndingActivated}
                  control={control}
                  variant="outlined"
                  label="sendEmailProgramEndingActivated"
                  name="sendEmailProgramEndingActivated"
                  fullWidth
                />
              </Box>
              <Box sx={{ padding: '8px 0px', display: 'flex', maxWidth: '500px', height: '70px' }}>
                <Typography
                  style={{
                    color: '#000000DE',
                    fontSize: '12px',
                    fontWeight: 500,
                    lineHeight: '32px',
                  }}>
                  {'Optional Scope'}
                </Typography>
                <Switch
                  ref={refOptionalScope}
                  control={control}
                  variant="outlined"
                  label="optionalScope"
                  name="optionalScope"
                  fullWidth
                />
              </Box>
              <Box sx={{ padding: '8px 0px', display: 'flex', maxWidth: '500px', height: '70px' }}>
                <Input
                  ref={refSelfRegistrationSendEmailProvider}
                  control={control}
                  variant="outlined"
                  label="selfRegistrationSendEmailProvider "
                  name="selfRegistrationSendEmailProvider"
                  fullWidth
                />
              </Box>
              <Box sx={{ padding: '8px 0px', display: 'flex', maxWidth: '500px', height: '70px' }}>
                <Input
                  ref={refExportDocumentLink}
                  control={control}
                  variant="outlined"
                  label="Export Document Link"
                  name="exportDocumentLink"
                  fullWidth
                />
              </Box>
              <Box
                sx={{
                  padding: '8px 0px',
                  height: '40px',
                  display: 'flex',
                  margin: '0px 0px',
                  backgroundColor: 'white',
                }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    navigate(-1);
                  }}
                  size="large">
                  {'BACK'}
                </Button>
                <Box sx={{ width: '33px' }} />
                <Button
                  variant="contained"
                  sx={{ backgroundColor: 'primary.dark', width: '80px' }}
                  onClick={() => {
                    submitEditCompanyWithNavigate();
                  }}
                  size="large">
                  {isLoadingEdit ? <CircularProgress size="1rem" color="secondary" /> : 'SAVE'}
                </Button>
              </Box>
            </Box>
          )}
        </Stack>
      </Stack>
    </>
  );
};

export default EditCompany;
